
import { useState, useEffect, useCallback } from "react";
import { ReturnForm } from "./ReturnForm";
import { toast } from "react-toastify";
import { useParams, useLocation } from "react-router-dom"; // Added useLocation for query params

// Main component for adding a new return
const AddReturn = () => {
  const { returnId: paramReturnId } = useParams(); // Get returnId from URL params if in edit mode
  const location = useLocation(); // For query parameters like ?type=supplier

  const [invoiceNo, setInvoiceNo] = useState("");
  const [purchaseId, setPurchaseId] = useState("");
  const [billData, setBillData] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [returnQuantities, setReturnQuantities] = useState({});
  const [adjustmentType, setAdjustmentType] = useState("stock"); // Default is "stock"
  const [note, setNote] = useState("");

  const [returnDate, setReturnDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeSearch, setActiveSearch] = useState(null); // 'customer' or 'manufacturer'
  const [isEditMode, setIsEditMode] = useState(false);

  const API_KEY =
    process.env.REACT_APP_API_KEY ||
    "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
  const API_BASE_URL = "https://api.muktihospital.com";

  // Helper function to get local date string (YYYY-MM-DD)
  const getLocalDateString = useCallback(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  // Helper function to get local date time ISO string with +06:00 offset
  const getLocalDateTimeISO = useCallback((dateString = null) => {
    let date;
    if (dateString) {
      if (dateString.includes("T") || dateString.includes("Z")) {
        date = new Date(dateString);
      } else {
        const [year, month, day] = dateString.split("-").map(Number);
        date = new Date(year, month - 1, day);
      }
    } else {
      date = new Date();
    }

    if (isNaN(date.getTime())) {
      console.error("Invalid Date object created in getLocalDateTimeISO");
      return null;
    }

    // Adjust for +06:00 offset (Bangladesh Standard Time)
    // The previous implementation was complex and might not correctly handle daylight saving or edge cases.
    // A simpler approach for a fixed offset like +06:00 is to convert to UTC and then add the offset.
    // However, toISOString() uses UTC. If your backend specifically expects +06:00,
    // you need to manually construct the string or use a library.
    // For simplicity and common API compatibility, it's often best to send in UTC ('Z' ending)
    // and let the backend handle timezone conversions if needed.
    // If +06:00 is strictly required, let's re-add a more robust custom string generation.

    // This approach correctly generates an ISO string with a +06:00 offset
    const userOffsetMinutes = date.getTimezoneOffset(); // Difference in minutes between UTC and local time
    const userOffsetMs = userOffsetMinutes * 60 * 1000;
    const dateInUTC = date.getTime() + userOffsetMs; // Convert to UTC timestamp

    const targetOffsetHours = 6; // +06:00
    const targetOffsetMs = targetOffsetHours * 60 * 60 * 1000;

    const finalDate = new Date(dateInUTC + targetOffsetMs);

    const year = finalDate.getFullYear();
    const month = String(finalDate.getMonth() + 1).padStart(2, "0");
    const day = String(finalDate.getDate()).padStart(2, "0");
    const hours = String(finalDate.getHours()).padStart(2, "0");
    const minutes = String(finalDate.getMinutes()).padStart(2, "0");
    const seconds = String(finalDate.getSeconds()).padStart(2, "0");
    const milliseconds = String(finalDate.getMilliseconds()).padStart(3, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}+06:00`;
  }, []);

  // Helper function to normalize date to local day (midnight of that day)
  const normalizeDateToLocalDay = useCallback((dateInput) => {
    if (!dateInput) return null;

    let date;
    if (dateInput instanceof Date) {
      date = dateInput;
    } else if (typeof dateInput === "string") {
      if (dateInput.includes("T") || dateInput.includes("Z")) {
        date = new Date(dateInput);
      } else {
        const [year, month, day] = dateInput.split("-").map(Number);
        date = new Date(year, month - 1, day);
      }
    } else {
      return null;
    }

    if (isNaN(date.getTime())) return null;

    const localYear = date.getFullYear();
    const localMonth = date.getMonth();
    const localDay = date.getDate();

    return new Date(localYear, localMonth, localDay);
  }, []);

  // Helper function to check if item is expired
  const isItemExpired = useCallback(
    (item) => {
      if (!item?.expiryDate) return false;

      const currentDateNormalized = normalizeDateToLocalDay(new Date());
      const expirationDateNormalized = normalizeDateToLocalDay(
        item.expiryDate
      );

      if (!currentDateNormalized || !expirationDateNormalized) {
        console.warn(
          "Could not normalize dates for expiry check:",
          item.medicineName,
          item.expiryDate
        );
        return false;
      }

      const isExpired = expirationDateNormalized < currentDateNormalized;

      if (process.env.NODE_ENV === "development") {
        // console.log(
        //   "Item:",
        //   item.medicineName,
        //   "Expiry Date (Raw):",
        //   item.expiryDate,
        //   "Expiry Date (Normalized):",
        //   expirationDateNormalized?.toISOString(),
        //   "Current Date (Normalized):",
        //   currentDateNormalized?.toISOString(),
        //   "Is Expired:",
        //   isExpired
        // );
      }

      return isExpired;
    },
    [normalizeDateToLocalDay]
  );

  // Check if there are valid return quantities
  const hasValidReturnQuantities =
    billData?.items?.some(
      (item) =>
        (returnQuantities[item.id] || 0) > 0 &&
        !isItemExpired(item) &&
        (returnQuantities[item.id] || 0) <= item.stockQty // Ensure return quantity does not exceed available stock
    ) || false;

  // Helper function for making API calls
  const makeAPICall = useCallback(
    async (url, options = {}) => {
      try {
        // console.log(`Making API call to: ${url}`, options);

        const response = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY,
            ...options.headers,
          },
          ...options,
        });

        // console.log(
        //   `API Response Status: ${response.status} ${response.statusText}`
        // );

        const responseText = await response.text();
        // console.log(`API Response Body: ${responseText.slice(0, 500)}...`);

        if (!response.ok) {
          let errorMessage;
          try {
            const errorData = JSON.parse(responseText);
            errorMessage =
              errorData.error || `HTTP ${response.status}: ${response.statusText}`;
          } catch {
            errorMessage = `HTTP ${response.status}: ${response.statusText} - ${responseText}`;
          }
          throw new Error(errorMessage);
        }

        return JSON.parse(responseText);
      } catch (error) {
        console.error(`API call failed for ${url}:`, error);
        throw error;
      }
    },
    [API_KEY]
  );

  // New function to fetch and calculate live stock from all purchases
  const getLiveStockFromAllPurchases = useCallback(
    (allPurchases, medicineId, medicineName) => {
      let totalAvailableQuantity = 0;
      if (!allPurchases || !Array.isArray(allPurchases)) {
        console.warn("No purchase data provided to getLiveStockFromAllPurchases.");
        return 0;
      }

      totalAvailableQuantity = allPurchases.reduce((sum, purchase) => {
        let purchaseItems = purchase.items;
        if (typeof purchaseItems === "string") {
          try {
            purchaseItems = JSON.parse(purchaseItems || "[]");
          } catch {
            purchaseItems = [];
          }
        }
        if (!Array.isArray(purchaseItems)) return sum;
        const itemQuantity = purchaseItems.reduce((itemSum, item) => {
          if (
            (medicineId && item.medicineId === medicineId) ||
            (medicineName &&
              item.medicineName?.toLowerCase().trim() ===
                medicineName.toLowerCase().trim())
          ) {
            return itemSum + (parseInt(item.availableQuantity) || 0);
          }
          return itemSum;
        }, 0);
        return sum + itemQuantity;
      }, 0);
      return totalAvailableQuantity;
    },
    []
  );

  // Function to load return data for edit mode
  const loadReturnData = useCallback(
    async (returnId, type) => {
      setLoading(true);
      setError("");
      setSuccessMessage("");
      try {
        const urlType = type === "customer" ? "CustomerReturn" : "SupplierReturn";
        const response = await makeAPICall(
          `${API_BASE_URL}/api/return/${urlType}/${returnId}`,
          { method: "GET" }
        );
        const data = response;
        // console.log("Loaded return data (from API):", data);

        if (data.return) {
          const returnData = data.return;
          setActiveSearch(urlType === "CustomerReturn" ? "customer" : "manufacturer");

          let allPurchases = [];
          try {
            const allPurchasesResponse = await makeAPICall(
              `${API_BASE_URL}/api/medicine-purchase/all`,
              { method: "GET" }
            );
            if (
              allPurchasesResponse.purchases &&
              Array.isArray(allPurchasesResponse.purchases)
            ) {
              allPurchases = allPurchasesResponse.purchases;
            } else {
              console.warn(
                "API for all purchases did not return an array:",
                allPurchasesResponse
              );
              setError("স্টক ডেটা লোড করতে সমস্যা হয়েছে। কিছু তথ্য অসম্পূর্ণ হতে পারে।");
            }
          } catch (purchaseFetchError) {
            console.error(
              "Failed to fetch all purchase data for live stock calculation:",
              purchaseFetchError
            );
            setError("স্টক ডেটা লোড করতে সমস্যা হয়েছে। কিছু তথ্য অসম্পূর্ণ হতে পারে।");
          }

          let processedItems = [];
          let billCreatedAt = null;

          if (urlType === "CustomerReturn") {
            const fetchedOriginalItems = returnData.pharmacyBill?.items || [];
            billCreatedAt = returnData.pharmacyBill?.createdAt;

            processedItems = await Promise.all(
              fetchedOriginalItems.map(async (originalBillItem) => {
                const liveStockQty = getLiveStockFromAllPurchases(
                  allPurchases,
                  originalBillItem.id,
                  originalBillItem.medicineName
                );

                const expiryDateStr = originalBillItem?.expiryDate
                  ? new Date(originalBillItem.expiryDate).toISOString()
                  : null;
                return {
                  id: originalBillItem.id, // Medicine ID
                  medicineName: originalBillItem?.medicineName || "Unknown",
                  quantity: originalBillItem?.quantity || 0, // Original sold quantity
                  salePrice: originalBillItem?.salePrice || 0,
                  tax: originalBillItem?.tax || 0,
                  stockQty: liveStockQty, // Current available stock
                  expiryDate: expiryDateStr,
                  purchaseId: originalBillItem?.purchaseId || null,
                  unitPrice: originalBillItem?.salePrice || 0,
                };
              })
            );
            setCustomerName(returnData.customerName || "");
            setSupplierName("");
          } else {
            // SupplierReturn
            const fetchedOriginalPurchaseItems = Array.isArray(returnData.purchase?.items)
              ? returnData.purchase.items
              : returnData.purchase?.items
              ? JSON.parse(returnData.purchase.items) // Handle stringified JSON
              : [];
            billCreatedAt = returnData.purchase?.createdAt;
            // console.log("Original Purchase Items (for SupplierReturn load):", fetchedOriginalPurchaseItems);

            processedItems = await Promise.all(
              fetchedOriginalPurchaseItems.map(async (originalPurchaseItem) => {
                const expiryDateStr = originalPurchaseItem?.expiryDate
                  ? new Date(originalPurchaseItem.expiryDate).toISOString()
                  : null;

                const unitsPerBox = originalPurchaseItem?.unitPerBox || 1; // Use unitPerBox from item for unitsPerBox
                const unitPrice =
                  (originalPurchaseItem?.unitPerBox && originalPurchaseItem.unitPerBox > 0)
                    ? (originalPurchaseItem?.supplierPrice || 0) / originalPurchaseItem.unitPerBox
                    : (originalPurchaseItem?.boxPrice || 0) / unitsPerBox; // Fallback

                // IMPORTANT: Adjust this based on your API's 'quantity' for purchased items
                // If originalPurchaseItem.quantity is number of BOXES, use:
                // const originalSingleQuantity = (originalPurchaseItem?.quantity || 0) * unitsPerBox;
                // If originalPurchaseItem.quantity is total SINGLE UNITS, use:
                const originalSingleQuantity = originalPurchaseItem?.quantity || 0;

                const liveStockQty = getLiveStockFromAllPurchases(
                  allPurchases,
                  originalPurchaseItem.medicineId || originalPurchaseItem.id, // Use medicineId for consistency
                  originalPurchaseItem.medicineName
                );

                return {
                  id: originalPurchaseItem.medicineId || originalPurchaseItem.id, // Medicine ID
                  medicineName: originalPurchaseItem?.medicineName || "Unknown",
                  quantity: originalSingleQuantity, // Original purchased quantity (in single units)
                  salePrice: unitPrice, // This is the unit price for display/calculation
                  tax: originalPurchaseItem?.tax || 0,
                  totalAmount: (originalPurchaseItem?.amount || 0) + (originalPurchaseItem?.taxAmount || 0), // Original total amount for purchased item
                  stockQty: liveStockQty, // Live available stock
                  expiryDate: expiryDateStr,
                  purchaseId: returnData.billId, // This should be the ID of the original purchase from which item is returned
                  boxPrice: originalPurchaseItem?.boxPrice || 0,
                  boxQty: originalPurchaseItem?.boxQty || 1, // Original box quantity from purchase item
                  supplierPrice: originalPurchaseItem?.supplierPrice || 0,
                  unitPerBox: originalPurchaseItem?.unitPerBox || 1,
                  unitPrice: unitPrice, // Calculated unit price
                };
              })
            );
            setSupplierName(returnData.customerName || ""); // returnData.customerName holds supplier name for supplier return
            setCustomerName("");
          }

          const normalizedBillCreatedAt = billCreatedAt
            ? new Date(billCreatedAt).toISOString().split("T")[0]
            : null;

          setBillData({
            items: processedItems,
            billNumber: returnData.billNumber, // This is the original purchase/bill number
            customerName: returnData.customerName, // This is the supplier/customer name for the return
            createdAt: normalizedBillCreatedAt, // Original purchase/bill date
            billId: returnData.billId, // This is the original purchase/bill ID
            supplierName: urlType === "SupplierReturn" ? returnData.customerName : "", // Ensure supplierName is set in billData
          });

          // Initialize returnQuantities based on actual returned quantities in 'returnData.items'
          const initialReturnQuantities = returnData.items.reduce((acc, item) => {
            const itemKey = item.itemId || item.purchaseItemId; // Key for the returned item
            let returnQuantity = item.quantity || 0; // Quantity of returned item (from returnData)

            if (urlType === "SupplierReturn") {
              // For supplier return, if item.quantity in API is in boxes, convert to single units for UI
              const matchingProcessedItem = processedItems.find(
                (pi) => (pi.id === itemKey) // Use 'id' from processedItems (which is medicineId)
              );
              if (matchingProcessedItem) {
                const unitsPerBox = matchingProcessedItem.unitPerBox || 1;
                returnQuantity = item.quantity * unitsPerBox; // Convert returned boxes to single units for UI
              }
            }
            return {
              ...acc,
              [itemKey]: returnQuantity,
            };
          }, {});

          setReturnQuantities(initialReturnQuantities);
          setNote(returnData.note || "");
          setAdjustmentType(returnData.adjustmentType || "stock");

          if (returnData.returnedAt) {
            const returnedDate = new Date(returnData.returnedAt);
            if (!isNaN(returnedDate.getTime())) {
              const year = returnedDate.getFullYear();
              const month = String(returnedDate.getMonth() + 1).padStart(2, "0");
              const day = String(returnedDate.getDate()).padStart(2, "0");
              setReturnDate(`${year}-${month}-${day}`);
            } else {
              setReturnDate(getLocalDateString());
            }
          } else {
            setReturnDate(getLocalDateString());
          }

          setIsEditMode(true);
        } else {
          setError(data.error || "Failed to load return data");
        }
      } catch (err) {
        setError("Error loading return data: " + err.message);
        console.error("Load Return Error:", err);
      } finally {
        setLoading(false);
      }
    },
    [API_BASE_URL, getLocalDateString, makeAPICall, getLiveStockFromAllPurchases]
  );

  const handleCancel = useCallback(() => {
    setInvoiceNo("");
    setPurchaseId("");
    setBillData(null);
    setCustomerName("");
    setSupplierName("");
    setReturnQuantities({});
    setNote("");
    setReturnDate(getLocalDateString());
    setError("");
    setSuccessMessage("");
    setActiveSearch(null); // Reset activeSearch
    setAdjustmentType("stock");
    setIsEditMode(false);
  }, [getLocalDateString]);

  // Handle search logic
  const handleSearch = async (e, type) => {
    e.preventDefault();
    if (type === "customer" && !invoiceNo) {
      setError("Please enter an invoice number");
      return;
    }
    if (type === "manufacturer" && !purchaseId) {
      setError("Please enter a purchase ID");
      return;
    }

    setLoading(true);
    setError("");
    setBillData(null);
    setCustomerName("");
    setSupplierName("");
    setSuccessMessage("");
    setNote("");
    setReturnQuantities({});
    setAdjustmentType("stock");
    setReturnDate(getLocalDateString());
    setActiveSearch(type); // Set activeSearch here
    setIsEditMode(false);

    try {
      let allPurchases = [];
      try {
        const allPurchasesResponse = await makeAPICall(
          `${API_BASE_URL}/api/medicine-purchase/all`,
          { method: "GET" }
        );
        if (
          allPurchasesResponse.purchases &&
          Array.isArray(allPurchasesResponse.purchases)
        ) {
          allPurchases = allPurchasesResponse.purchases;
        } else {
          console.warn(
            "API for all purchases did not return an array:",
            allPurchasesResponse
          );
          setError("স্টক ডেটা লোড করতে সমস্যা হয়েছে। কিছু তথ্য অসম্পূর্ণ হতে পারে।");
        }
      } catch (purchaseFetchError) {
        console.error(
          "Failed to fetch all purchase data for live stock calculation:",
          purchaseFetchError
        );
        setError("স্টক ডেটা লোড করতে সমস্যা হয়েছে। কিছু তথ্য অসম্পূর্ণ হতে পারে।");
      }

      const url =
        type === "customer"
          ? `${API_BASE_URL}/api/medicine-bill/billNumber/${invoiceNo}`
          : `${API_BASE_URL}/api/return/purchaseId/${purchaseId}`;

      const response = await makeAPICall(url, { method: "GET" });
      const data = response;
      // console.log(`${type} API Response:`, data);

      if (data.bill || data.purchase) {
        if (type === "customer") {
          const billItems = data.bill.items;
          const itemsWithLiveStock = await Promise.all(
            billItems.map(async (item) => {
              const liveStockQty = getLiveStockFromAllPurchases(
                allPurchases,
                item.id,
                item.medicineName
              );

              const expiryDateStr = item.expiryDate
                ? new Date(item.expiryDate).toISOString()
                : null;
              return {
                ...item,
                stockQty: liveStockQty,
                expiryDate: expiryDateStr,
              };
            })
          );

          const normalizedBillCreatedAt = data.bill.createdAt
            ? new Date(data.bill.createdAt).toISOString().split("T")[0]
            : null;

          setBillData({
            ...data.bill,
            items: itemsWithLiveStock,
            createdAt: normalizedBillCreatedAt,
            supplierName: "", // Ensure supplierName is empty for customer return
          });
          setCustomerName(data.bill.customerName || "");
          setReturnQuantities(
            itemsWithLiveStock.reduce((acc, item) => {
              acc[item.id] = 0;
              return acc;
            }, {})
          );
          const expiredItems = itemsWithLiveStock.filter(isItemExpired);
          if (expiredItems.length > 0) {
            setError(
              `আইটেম ${expiredItems
                .map((item) => item.medicineName)
                .join(", ")} এর মেয়াদ উত্তীর্ণ হয়েছে এবং ফেরত দেওয়া যাবে না।`
            );
          }
        } else {
          // Manufacturer/Supplier search
          const purchase = data.purchase;
          if (!purchase) {
            setError("এই আইডি-র জন্য ক্রয় পাওয়া যায়নি।");
            setLoading(false);
            return;
          }

          const items = await Promise.all(
            (Array.isArray(purchase.items) ? purchase.items : JSON.parse(purchase.items || "[]")).map(async (item) => {
              const expiryDateStr = item.expiryDate
                ? new Date(item.expiryDate).toISOString()
                : null;

              const unitsPerBox = item.unitPerBox || 1;
              const unitPrice =
                (item.unitPerBox && item.unitPerBox > 0)
                  ? (item.supplierPrice || 0) / item.unitPerBox
                  : (item.boxPrice || 0) / unitsPerBox; // Fallback

              // IMPORTANT: Adjust this based on your API's 'quantity' for purchased items
              // If item.quantity is number of BOXES, use:
              // const originalSingleQuantity = (item.quantity || 0) * unitsPerBox;
              // If item.quantity is total SINGLE UNITS, use:
              const originalSingleQuantity = item.quantity || 0;

              const liveStockQty = getLiveStockFromAllPurchases(
                allPurchases,
                item.medicineId,
                item.medicineName
              );

              return {
                id: item.medicineId,
                medicineName: item.medicineName,
                quantity: originalSingleQuantity,
                salePrice: unitPrice, // Display/use this as the unit price in form
                tax: item.tax,
                totalAmount: (item.amount || 0) + (item.taxAmount || 0), // Original purchase item's total
                purchaseId: purchase.id,
                stockQty: liveStockQty, // Current available stock
                expiryDate: expiryDateStr,
                boxPrice: item.boxPrice || 0,
                boxQty: item.boxQty || 1,
                supplierPrice: item.supplierPrice || 0,
                unitPerBox: item.unitPerBox || 1,
                unitPrice: unitPrice, // Set calculated unit price
              };
            })
          );

          const expiredItems = items.filter(isItemExpired);
          if (expiredItems.length > 0) {
            setError(
              `আইটেম ${expiredItems
                .map((item) => item.medicineName)
                .join(", ")} এর মেয়াদ উত্তীর্ণ হয়েছে এবং ফেরত দেওয়া যাবে না।`
            );
            setLoading(false);
            return;
          }

          const normalizedPurchaseCreatedAt = purchase.createdAt
            ? new Date(purchase.createdAt).toISOString().split("T")[0]
            : null;

          setBillData({
            billNumber: purchase.billNo,
            customerName: "", // Ensure customerName is empty for supplier return
            supplierName: purchase.supplier.supplierName,
            createdAt: normalizedPurchaseCreatedAt,
            items,
            keywords: [],
            billId: purchase.id,
          });
          setSupplierName(purchase.supplier.supplierName);
          setReturnQuantities(
            items.reduce(
              (acc, item) => ({
                ...acc,
                [item.id]: 0,
              }),
              {}
            )
          );
        }
      } else {
        setError(
          data.error ||
            `${type === "customer" ? "বিল" : "ক্রয়"} পাওয়া যায়নি`
        );
      }
    } catch (err) {
      setError(
        `একটি ত্রুটি ঘটেছে ${
          type === "customer" ? "বিল" : "ক্রয়"
        } লোড করার সময়: ` + err.message
      );
      console.error(`${type} Fetch Error:`, err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Customer Submit Return
  const handleCustomerSubmitReturn = async (e) => {
    e.preventDefault();
    if (!billData) {
      setError("কোনো বিল ডেটা উপলব্ধ নেই।");
      return;
    }
    if (!note.trim()) {
      setError("নোট আবশ্যক।");
      return;
    }
    if (!hasValidReturnQuantities) {
      setError("অন্তত একটি বৈধ, অ-মেয়াদোত্তীর্ণ আইটেমের জন্য রিটার্ন পরিমাণ প্রবেশ করুন।");
      return;
    }
    if (!billData.billNumber) {
      setError("বিল নম্বর অনুপস্থিত।");
      return;
    }
    if (!["stock", "wastage"].includes(adjustmentType)) {
      setError("অবৈধ অ্যাডজাস্টমেন্ট টাইপ।");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const expiredItems = billData.items.filter(isItemExpired);
      if (expiredItems.length > 0) {
        setError(
          `রিটার্ন প্রক্রিয়া করা যাবে না: আইটেম ${expiredItems
            .map((item) => item.medicineName)
            .join(", ")} এর মেয়াদ উত্তীর্ণ হয়েছে`
        );
        setLoading(false);
        return;
      }

      const returnItems = billData.items
        .map((item) => {
          const quantity = returnQuantities[item.id];
          if (quantity > 0 && quantity <= item.stockQty) {
            return {
              itemId: item.id,
              quantity,
              // Include unitPrice and tax for accurate backend calculation/record
              unitPrice: item.salePrice,
              tax: item.tax,
            };
          }
          return null;
        })
        .filter((item) => item !== null);

      if (returnItems.length === 0) {
        setError("অন্তত একটি বৈধ, অ-মেয়াদোত্তীর্ণ আইটেমের জন্য রিটার্ন পরিমাণ প্রবেশ করুন।");
        setLoading(false);
        return;
      }

      const totalTax = returnItems
        .reduce((sum, rItem) => {
          // originalItem is needed to get salePrice and tax if they are not in rItem directly
          const originalItem = billData.items.find((item) => item.id === rItem.itemId);
          if (!originalItem) return sum;
          return sum + rItem.quantity * originalItem.salePrice * ((originalItem.tax || 0) / 100);
        }, 0)
        .toFixed(2);

      const netReturn = returnItems
        .reduce((sum, rItem) => {
          const originalItem = billData.items.find((item) => item.id === rItem.itemId);
          if (!originalItem) return sum;
          const itemTotal = rItem.quantity * originalItem.salePrice * (1 + ((originalItem.tax || 0) / 100));
          return sum + itemTotal;
        }, 0)
        .toFixed(2);

      const payload = {
        billNumber: billData.billNumber,
        adjustmentType,
        totalDeduction: "0.00", // Assuming no deductions for now
        totalTax,
        netReturn,
        note: note.trim(),
        returnedAt: getLocalDateTimeISO(returnDate),
        returnItems, // returnItems already has unitPrice and tax if added above
        customerName: customerName.trim(),
      };

      // console.log("Customer Return Payload:", JSON.stringify(payload, null, 2));

      const response = await makeAPICall(
        `${API_BASE_URL}/api/return/invoice`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      // console.log("Customer Return Response:", response);

      if (response) {
        toast.success("গ্রাহকের রিটার্ন সফলভাবে প্রক্রিয়া করা হয়েছে", {
          position: "top-right",
          autoClose: 3000,
        });
        handleCancel();
      } else {
        setError("গ্রাহকের রিটার্ন প্রক্রিয়া করতে ব্যর্থ।");
      }
    } catch (err) {
      setError(
        "গ্রাহকের রিটার্ন প্রক্রিয়া করার সময় একটি ত্রুটি ঘটেছে: " + err.message
      );
      console.error("Customer Return Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Supplier Submit Return
  const handleSupplierSubmitReturn = async (e) => {
    e.preventDefault();
    if (!billData) {
      setError("কোনো স্টক ডেটা উপলব্ধ নেই।");
      return;
    }
    if (!note.trim()) {
      setError("নোট আবশ্যক।");
      return;
    }
    if (!supplierName.trim()) {
      setError("সরবরাহকারীর নাম আবশ্যক।");
      return;
    }
    if (!hasValidReturnQuantities) {
      setError(
        "অন্তত একটি বৈধ, অ-মেয়াদোত্তীর্ণ আইটেমের জন্য রিটার্ন পরিমাণ প্রবেশ করুন।"
      );
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const expiredItems = billData.items.filter(isItemExpired);
      if (expiredItems.length > 0) {
        setError(
          `রিটার্ন প্রক্রিয়া করা যাবে না: আইটেম ${expiredItems
            .map((item) => item.medicineName)
            .join(", ")} এর মেয়াদ উত্তীর্ণ হয়েছে`
        );
        setLoading(false);
        return;
      }

      const returnItems = billData.items
        .map((item) => {
          const singleUnitsQuantity = returnQuantities[item.id];
          if (singleUnitsQuantity > 0 && singleUnitsQuantity <= item.stockQty) {
            // Re-calculate unitPrice here to ensure consistency, though it should already be correct in item.unitPrice
            const unitsPerBox = item.unitPerBox || 1; // Use item's unitPerBox directly
            const unitPrice =
              (item.unitPerBox && item.unitPerBox > 0)
                ? (item.supplierPrice || 0) / item.unitPerBox
                : (item.boxPrice || 0) / unitsPerBox; // Fallback

            return {
              itemId: item.id,
              quantity: singleUnitsQuantity, // Quantity in single units for payload
              unitPrice: unitPrice, // Calculated unit price for this item
              tax: item.tax,
              // supplierPrice: item.supplierPrice || 0, // Optionally send these if backend needs them
              // boxPrice: item.boxPrice || 0,
              // boxQty: unitsPerBox,
              // unitPerBox: item.unitPerBox || 1,
            };
          }
          return null;
        })
        .filter((item) => item !== null);

      if (returnItems.length === 0) {
        setError(
          "অন্তত একটি বৈধ, অ-মেয়াদোত্তীর্ণ আইটেমের জন্য রিটার্ন পরিমাণ প্রবেশ করুন।"
        );
        setLoading(false);
        return;
      }

      // Calculate totals based on returnItems (which now contain single units and unitPrice)
      const totalTax = returnItems
        .reduce((sum, rItem) => {
          const itemTaxAmount =
            rItem.quantity * rItem.unitPrice * ((rItem.tax || 0) / 100);
          return sum + itemTaxAmount;
        }, 0)
        .toFixed(2);

      const netReturn = returnItems
        .reduce((sum, rItem) => {
          const itemTotal =
            rItem.quantity * rItem.unitPrice * (1 + (rItem.tax || 0) / 100);
          return sum + itemTotal;
        }, 0)
        .toFixed(2);

      const payload = {
        purchaseId: billData.billId,
        billNumber: billData.billNumber,
        returnItems: returnItems, // returnItems already has unitPrice and tax
        adjustmentType,
        supplierName: supplierName.trim(),
        totalDeduction: "0.00",
        totalTax,
        netReturn,
        note: note.trim(),
        returnedAt: getLocalDateTimeISO(returnDate),
      };

      // console.log("Supplier Return Payload:", JSON.stringify(payload, null, 2));

      const response = await makeAPICall(
        `${API_BASE_URL}/api/return/manufacturer`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      // console.log("Supplier Return Response:", response);

      if (response) {
        toast.success("সরবরাহকারীর রিটার্ন সফলভাবে প্রক্রিয়া করা হয়েছে", {
          position: "top-right",
          autoClose: 3000,
        });
        handleCancel();
      } else {
        setError("সরবরাহকারীর রিটার্ন প্রক্রিয়া করতে ব্যর্থ।");
      }
    } catch (err) {
      setError(
        "সরবরাহকারীর রিটার্ন প্রক্রিয়া করার সময় একটি ত্রুটি ঘটেছে: " +
          err.message
      );
      console.error("Supplier Return Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Effect to load return data on component mount or ID/type change
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const type = queryParams.get("type"); // 'customer' or 'supplier'
    const returnId = queryParams.get("returnId");

    if (returnId && type) {
      loadReturnData(returnId, type);
    }
  }, [loadReturnData, location.search]); // Depend on location.search to re-run if query params change

  return (
    <div>
      {!billData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <div className="bg-white shadow-sm rounded-lg pb-2">
            <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Return From Customer
              </h2>
            </div>
            <form
              onSubmit={(e) => handleSearch(e, "customer")}
              className="p-5 flex items-center gap-4"
            >
              <div className="flex items-center gap-4 flex-1">
                <label htmlFor="invoice-no" className="label mb-0 shrink-0">
                  Invoice No: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Invoice No"
                  id="invoice-no"
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
                disabled={loading || activeSearch === "manufacturer"}
              >
                {loading && activeSearch === "customer" ? "Searching..." : "Search"}
              </button>
            </form>
            {error && activeSearch === "customer" && (
              <p className="text-red-500 px-5">{error}</p>
            )}
          </div>
          <div className="bg-white shadow-sm rounded-lg pb-2">
            <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Return To Supplier
              </h2>
            </div>
            <form
              onSubmit={(e) => handleSearch(e, "manufacturer")}
              className="p-5 flex items-center gap-4"
            >
              <div className="flex items-center gap-4 flex-1">
                <label htmlFor="purchase-id" className="label mb-0 shrink-0">
                  Purchase Id: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Purchase Id"
                  id="purchase-id"
                  value={purchaseId}
                  onChange={(e) => setPurchaseId(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
                disabled={loading || activeSearch === "customer"}
              >
                {loading && activeSearch === "manufacturer" ? "Searching..." : "Search"}
              </button>
            </form>
            {error && activeSearch === "manufacturer" && (
              <p className="text-red-500 px-5">{error}</p>
            )}
          </div>
        </div>
      )}
      {/* Conditionally render ReturnForm based on billData and activeSearch */}
      {billData && (
        <ReturnForm
          billData={billData}
          customerName={customerName}
          supplierName={supplierName}
          returnQuantities={returnQuantities}
          note={note}
          adjustmentType={adjustmentType}
          returnDate={returnDate}
          setCustomerName={setCustomerName}
          setSupplierName={setSupplierName}
          setReturnQuantities={setReturnQuantities}
          setNote={setNote}
          setAdjustmentType={setAdjustmentType}
          setReturnDate={setReturnDate}
          // Use activeSearch to determine which handleSubmit to use
          handleSubmit={
            activeSearch === "manufacturer"
              ? handleSupplierSubmitReturn
              : handleCustomerSubmitReturn
          }
          handleCancel={handleCancel}
          isCustomerReturn={activeSearch === "customer"}
          isSupplierReturn={activeSearch === "manufacturer"}
          loading={loading}
          error={error}
          successMessage={successMessage}
          isItemExpired={isItemExpired}
          hasValidReturnQuantities={hasValidReturnQuantities}
          isEditMode={isEditMode}
          setError={setError}
          getLocalDateTimeISO={getLocalDateTimeISO}
        />
      )}
    </div>
  );
};

export default AddReturn;

